<template>
    <div id="shopify-plan">
        <div v-if="settingsFetched && settings.plan" class="w-100">
            <div class="plan-detail" v-if="header">
                <strong>{{ $t("billing.yourCurrentPlan") }}: </strong>
                {{ `${settings.plan.selectedPlan.toUpperCase()} \t (${settings.plan.detail.maxAgentCount} Agent)` }}
            </div>
            <div class="plans" v-if="!approvedMode && !approving">
                <div class="plan" :class="{ selected: settings.plan.selectedPlan === 'free' }">
                    <div class="header">Free Plan</div>
                    <div class="plan-price-section">
                        <div class="plan-price-details">
                            <span class="amount" :class="{ tl: site.basePrice.currency === 'tl' }">0</span>
                            <i>
                                <i class="slash">/</i>
                                <i class="billed-month"
                                    >{{ $t("billing.month") }}
                                    <i class="billed-yearly hide"><br />{{ $t("billing.billedMonthToMonth") }}</i></i
                                >
                            </i>
                        </div>
                    </div>
                    <md-field class="selectAgentCount">
                        <md-select autocomplete="off" :value="1" disabled>
                            <md-option :value="1"> 1 {{ $t("billing.oneAgent") }}</md-option>
                        </md-select>
                    </md-field>
                    <md-button
                        class="md-raised md-primary w-100"
                        :disabled="selectPlanDisabled || settings.plan.selectedPlan === 'free'"
                        @click="selectPlan(true, 1)"
                        style="transform: scale(0.7); font-size: x-large; border-radius: 10%"
                        >{{
                            settings.plan.selectedPlan === "free"
                                ? $t("billing.yourCurrentPlan")
                                : $t("billing.selectFreePlan")
                        }}
                    </md-button>
                    <span class="text-succes">-</span>
                    <a-divider>{{ $t("billing.planDetails") }}</a-divider>
                    <div class="features">
                        <div class="feature">
                            <a-icon type="check" />
                            <span>1 {{ $t("billing.agent") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.videoCallUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.audioCallUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.screenSharingUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.fileTransferUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.autoChatAssignerUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.multiLanguageTranslationUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.detailedReporting") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.fullCustomizationUnlimited") }}</span>
                        </div>
                    </div>
                </div>
                <div class="plan" :class="{ selected: settings.plan.selectedPlan === 'pro' }">
                    <div class="header">Pro Plan</div>
                    <div class="plan-price-section">
                        <div class="plan-price-details">
                            <span class="amount" :class="{ tl: site.basePrice.currency === 'tl' }">{{
                                calculateAgentPrice(selectedAgentCount)
                            }}</span
                            ><i
                                ><i class="slash">/</i>
                                <i class="billed-month"
                                    >{{ $t("billing.month") }}
                                    <i class="billed-yearly hide"><br />{{ $t("billing.billedMonthToMonth") }}</i></i
                                ></i
                            >
                        </div>
                    </div>
                    <md-field class="selectAgentCount">
                        <md-select autocomplete="off" v-model="selectedAgentCount">
                            <md-option v-for="(count, i) of agentCounts" :value="count" :key="i">
                                {{ count }} {{ $t("billing.agent") }}</md-option
                            >
                        </md-select>
                    </md-field>
                    <md-button
                        class="md-raised md-primary w-100"
                        :disabled="
                            selectPlanDisabled ||
                            (settings.plan.selectedPlan === 'pro' &&
                                settings.plan.detail.maxAgentCount === selectedAgentCount)
                        "
                        @click="selectPlan(false, selectedAgentCount)"
                        style="transform: scale(0.7); font-size: x-large; border-radius: 10%"
                        >{{
                            settings.plan.selectedPlan === "pro" &&
                            settings.plan.detail.maxAgentCount === selectedAgentCount
                                ? $t("billing.yourCurrentPlan")
                                : $t("billing.selectProPlan")
                        }}
                    </md-button>
                    <span class="text-success">{{ $t("billing.days30TrialsAvailable") }}</span>
                    <a-divider>{{ $t("billing.planDetails") }}</a-divider>
                    <div class="features">
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.multiAgentUpTo", { agentCount: selectedAgentCount }) }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.videoCallUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.audioCallUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.screenSharingUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.fileTransferUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.autoChatAssignerUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.multiLanguageTranslationUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.detailedReporting") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.fullCustomizationUnlimited") }}</span>
                        </div>
                        <div class="feature">
                            <a-icon type="check" />
                            <span>{{ $t("billing.ChatBotUnlimited") }}</span>
                        </div>
                        <!--                        <div class="feature">-->
                        <!--                            <a-icon type="check" />-->
                        <!--                            <span>{{ $t("billing.days30TrialsAvailable") }}</span>-->
                        <!--                        </div>-->
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <md-progress-spinner :md-diameter="20" class="md-accent" md-mode="indeterminate"></md-progress-spinner>
        </div>

        <a-result :title="$t('billing.planUpdated')" v-if="approvedMode">
            <template #icon>
                <a-icon type="smile" theme="twoTone" />
            </template>
            <template #extra>
                <a-button type="primary" @click="approvedMode = false"> {{ $t("billing.done") }}</a-button>
            </template>
        </a-result>
        <a-alert
            v-if="approving"
            type="success"
            show-icon
            :message="$t('billing.planUpdating')"
            :description="$t('billing.planUpdatingDescription')"
            banner
        />
        <ComparePlans
            v-if="compareMode.enabled"
            :new-plan="compareMode.newPlan"
            :old-plan="compareMode.oldPlan"
            :on-approved="planChangeApproved"
            :on-cancel="planChangeRejected"
        />
    </div>
</template>

<script>
import log2 from "../../utils/log2";
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import { changePlan, changeShopifyPlan, shopifyPlanApproved } from "../../api/billing";
import { sendSocketMessage } from "../../utils/socketStream";
import { USER_API } from "../../api/user";
import ComparePlans from "./ComparePlans";

const log = log2("ShopifyBillingDetails");
export default {
    name: "ShopifyBillingDetails",
    components: {
        ComparePlans,
    },
    data() {
        return {
            selectedPlan: "pro",
            selectedAgentCount: 1,
            agentCounts: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200],
            selectPlanDisabled: false,
            approvedMode: false,
            approving: false,
            compareMode: {
                enabled: false,
                oldPlan: null,
                newPlan: null,
            },
        };
    },
    props: {
        header: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            settings: (state) => state.setting.settings,
            settingsFetched: (state) => state.setting.settings.initialized,
            isShopify: (state) => _.get(state.site.site, "partner.name") === "shopify",
        }),
        ...mapGetters({}),
    },
    methods: {
        ...mapActions({
            setSettings: "setting/setSettings",
        }),
        planChangeApproved() {
            this.compareMode = {
                ...this.compareMode,
                enabled: false,
            };
            const { plan, agentCount } = this.compareMode.newPlan;

            if (this.isShopify) {
                changeShopifyPlan(plan, agentCount)
                    .then((response) => {
                        if (response.status === 200) {
                            if (plan === "pro") {
                                const { appSubscription, confirmationUrl, userErrors } = response.data;
                                if (confirmationUrl) {
                                    window.location.href = confirmationUrl;
                                }
                            } else {
                                this.selectPlanDisabled = false;
                                this.showMessage("success", this.$t("billing.freePlanUpdated"));
                                this.setSettings({ settings: response.data });
                                this.selectedAgentCount = 2;
                            }
                            sendSocketMessage("admin settings-updated", {
                                siteId: this.site._id,
                            });
                        }
                        log("changeShopifyPlan", response);
                    })
                    .catch((err) => {
                        this.selectPlanDisabled = false;
                        log.error(err);
                        this.showMessage("error", this.$t("billing.errorOccurred"));
                    });
            } else {
                changePlan(plan, agentCount)
                    .then((response) => {
                        log("changePlan", response);
                        if (response.status === 200) {
                            this.selectPlanDisabled = false;
                            if (plan === "free") {
                                this.showMessage("success", this.$t("billing.freePlanUpdated"));
                            } else
                                this.showMessage(
                                    "success",
                                    `${this.$t("billing.proPlanUpdated", { agentCount: agentCount })}`
                                );
                            this.setSettings({ settings: response.data });
                            this.selectedAgentCount = plan === "free" ? 2 : agentCount;
                            sendSocketMessage("admin settings-updated", {
                                siteId: this.site._id,
                            });
                        } else {
                            this.showMessage("error", this.$t("billing.errorOccurred"));
                        }
                    })
                    .catch((err) => {
                        this.selectPlanDisabled = false;
                        log.error(err);
                        this.showMessage("error", this.$t("billing.errorOccurred"));
                    });
            }
        },
        planChangeRejected() {
            this.compareMode = {
                ...this.compareMode,
                enabled: false,
            };
            this.selectPlanDisabled = false;
        },
        async changePlan(plan, agentCount) {
            this.selectPlanDisabled = true;
            log("changePlan", { plan, agentCount });
            const oldPlanModel = this.settings.plan;
            const checkValid = async () => {
                const oldPlan = oldPlanModel.selectedPlan;
                if (oldPlan === "free") {
                    return {
                        valid: true,
                        new: { plan, agentCount },
                        old: { plan: oldPlan, agentCount: oldPlanModel.detail.maxAgentCount },
                    };
                }
                const usedAgentCredit = await USER_API.fetchActiveUserCount();
                if (agentCount >= usedAgentCredit)
                    return {
                        valid: true,
                        new: { plan, agentCount },
                        old: { plan: oldPlan, agentCount: oldPlanModel.detail.maxAgentCount },
                    };
                else
                    return {
                        valid: false,
                        new: { plan, agentCount },
                        old: { plan: oldPlan, agentCount: oldPlanModel.detail.maxAgentCount },
                        diff: usedAgentCredit - agentCount,
                    };
            };
            const validation = await checkValid().catch((err) => {
                log.error(err);
                this.showMessage("error", this.$t("billing.errorOccurred"));
                this.selectPlanDisabled = false;
            });
            if (oldPlanModel.detail.maxAgentCount === agentCount && oldPlanModel.selectedPlan === plan) {
                this.showMessage("warn", this.$t("billing.warnMessage"), 1);
                this.selectPlanDisabled = false;
                return;
            }

            if (!validation.valid) {
                this.selectPlanDisabled = false;

                this.showMessage(
                    "error",
                    `${this.$t("billing.canNotDwnGrade", {
                        agentCountFirst: validation.old.agentCount,
                        valNewPlnToUppr: validation.new.plan.toUpperCase(),
                        agentCountSecond: validation.new.agentCount,
                    })}
                          ${this.$t("billing.deleteWarning", { valDif: validation.diff })} `
                );
                return;
            }

            this.compareMode = {
                enabled: true,
                oldPlan: { plan: oldPlanModel.selectedPlan, agentCount: oldPlanModel.detail.maxAgentCount },
                newPlan: { plan, agentCount },
            };
        },
        calculateAgentPrice(count) {
            return parseFloat((count * this.site.basePrice.value).toFixed(2));
        },
        selectPlan(isFree, agentCount) {
            const plan = isFree ? "free" : "pro";
            this.changePlan(plan, agentCount);
        },
        showMessage(level, message, key = Math.random(), duration = 4) {
            this.$message[level]({
                content: message,
                key,
                duration,
            });
        },
    },
    mounted() {
        if (_.get(this.settings, "plan.detail.maxAgentCount")) {
            if (this.settings.plan.selectedPlan === "pro")
                this.selectedAgentCount = _.get(this.settings, "plan.detail.maxAgentCount");
        }
        const approvedChargeId = this.$route.query["charge_id"];
        const showMessage = (level, message) => this.showMessage(level, message, "plan_updated");
        if (approvedChargeId) {
            this.approvedMode = true;
            this.approving = true;
            shopifyPlanApproved(approvedChargeId)
                .then((res) => {
                    if (res.status === 200) {
                        showMessage("success", this.$t("billing.planUpdated"));
                        log("shopifyPlanApproved", res.data);
                        const settings = res.data;
                        this.selectedAgentCount = settings.plan.detail.maxAgentCount;
                        this.setSettings({ settings });
                        sendSocketMessage("user settings-updated", {
                            siteId: this.site._id,
                        });
                    } else {
                        showMessage("error", this.$t("billing.errorOccurred"));
                    }
                })
                .catch((err) => {
                    log.error("shopifyPlanApproved error", err);
                    showMessage("error", this.$t("billing.errorOccurred"));
                })
                .finally(() => {
                    this.$router.push("/billing");
                    this.approving = false;
                });
        }
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        settings: {
            handler: function (settings) {
                log("settings changed", settings);
                if (_.get(settings, "plan.detail.maxAgentCount")) {
                    if (settings.plan.selectedPlan === "pro") {
                        this.selectedAgentCount = _.get(settings, "plan.detail.maxAgentCount");
                        if (!this.agentCounts.includes(this.selectedAgentCount)) {
                            this.agentCounts.push(this.selectedAgentCount);
                        }
                    }
                }
            },
            deep: true,
        },
        selectedAgentCount(newValue) {
            log("selectedAgentCount changed", newValue);
        },
    },
};
</script>

<style lang="scss">
#shopify-plan,
.compareModal {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin-top: 10px;
    //min-height: 80vh;
    min-width: 80vw;
    padding: 3px;

    .plan-detail {
        font-size: 20px;
        background-color: #ededed;
        padding: 12px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .plans {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 5px;

        span.ant-divider-inner-text {
            font-size: 12px !important;
            color: #935774 !important;
        }

        .plan {
            display: flex;
            flex-direction: column;
            align-items: center;
            border: 1px solid #aa4b6b;
            width: 300px;
            min-height: 605px;
            margin-left: 20px;

            .selectAgentCount {
                width: 200px;
                padding: 0 !important;
                margin: 0 !important;
                min-height: 34px;
            }

            .features {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;

                .feature {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    margin: 1px 5px;
                    padding: 2px;

                    i {
                        color: green;
                    }

                    span {
                        margin-left: 5px;
                    }
                }
            }

            .header {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 10px;
                font-size: 20px;
                font-weight: 400;
                background: #aa4b6b; /* fallback for old browsers */
                background: -webkit-linear-gradient(
                    to bottom,
                    #3b8d99,
                    #6b6b83,
                    #aa4b6b
                ); /* Chrome 10-25, Safari 5.1-6 */
                background: linear-gradient(
                    to bottom,
                    #3b8d99,
                    #6b6b83,
                    #aa4b6b
                ); /* W3C, IE 10+/ Edge, Firefox 16+, Chrome 26+, Opera 12+, Safari 7+ */
                color: white;
                width: 100%;
            }

            .plan-price-details {
                padding: 0;
                font-size: 45px;
                line-height: 49px;
                font-weight: 400;
                font-family: "source-sans-pro", helvetica;
                color: #333;
                text-align: center;

                i {
                    font-weight: 600;
                    font-size: 16px;
                    font-style: normal;
                    font-family: myriad-pro;
                    line-height: 21px;
                }
            }

            .pricing-cycle-info {
                padding-bottom: 21px;
            }

            .amount {
                font-size: 45px;

                &:before {
                    content: "$";
                    font-size: 16px;
                    position: absolute;
                    font-weight: 400;
                    line-height: 21px;
                    margin-left: -10px;
                }
            }

            .amount.tl {
                &:before {
                    content: "₺";
                }
            }
        }
        .plan.new,
        .plan.old {
            min-height: 475px !important;
        }
        .plan.selected {
            border-right: 3px;
            border-bottom: 3px;
            box-shadow: #8c5a77 -9px 2px 9px 3px;
        }
    }
}
</style>
